import { useEffect, useRef, useState } from "react";
import { ArrowUp, FileEdit, Loader2, ShieldCheck } from "lucide-react";
import { emailApi, type ChatItem, type DraftInput, type EmailCohort, type Proposal } from "./api";
import DraftForm from "./draft-form";
import { Eyebrow, Notice, Panel, fmtDate } from "./shared";

type Props = { chat: ChatItem[]; cohorts: EmailCohort[]; seed?: Partial<DraftInput>; onRefresh: () => void; onSaved: (id: number) => void };

const STARTERS = [
  "Draft a short reminder for the current cohort about Thursday's founder call.",
  "Which queued emails cover similar ground and could be combined?",
  "Prepare a welcome note for investors added in the last week.",
];

export default function AssistantPanel({ chat, cohorts, seed, onRefresh, onSaved }: Props) {
  const [input, setInput] = useState("");
  const [pending, setPending] = useState<ChatItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ key: number; initial?: Partial<DraftInput> }>({ key: 0, initial: seed });
  const endRef = useRef<HTMLDivElement | null>(null);

  const items = [...chat, ...pending];
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [items.length]);

  const send = async (text: string) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setBusy(true); setError(null); setInput("");
    setPending([{ id: `local-${Date.now()}`, role: "user", content: msg, proposal: null, created_at: new Date().toISOString() }]);
    try {
      const r = await emailApi<{ reply: string; proposal: Proposal | null }>("/assistant", { method: "POST", body: JSON.stringify({ message: msg }) });
      setPending((p) => [...p, { id: `local-r-${Date.now()}`, role: "assistant", content: r.reply, proposal: r.proposal, created_at: new Date().toISOString() }]);
      await onRefresh();
      setPending([]);
    } catch (e) {
      setError(`${e instanceof Error ? e.message : "The assistant is unavailable."} You can still write the email by hand on the right.`);
      setInput(msg); setPending([]);
    } finally { setBusy(false); }
  };

  const useProposal = (p: Proposal) => {
    setForm((f) => ({ key: f.key + 1, initial: { subject: p.subject, bodyText: p.bodyText, to: p.to, cohortId: p.cohortId, request: p.request } }));
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <Panel className="flex flex-col min-h-[560px]">
        <div className="border-b border-border px-5 py-4">
          <Eyebrow>Email assistant</Eyebrow>
          <p className="mt-1 text-sm text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#FF5C00]" />It can read email history and cohorts and propose drafts. It cannot run code, approve or send anything.</p>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 max-h-[60vh]" data-testid="list-chat">
          {items.length === 0 && (
            <div className="space-y-3 py-6">
              <p className="font-serif text-xl">What needs to go out?</p>
              <p className="text-sm text-muted-foreground">Describe the email in plain words. You will always see an editable draft before anything is saved.</p>
              <div className="space-y-2">
                {STARTERS.map((s, i) => <button key={i} data-testid={`button-starter-${i}`} onClick={() => send(s)} className="block w-full text-left rounded-lg border border-border px-3 py-2 text-sm hover:border-[#FF5C00]/50 hover:bg-muted/50">{s}</button>)}
              </div>
            </div>
          )}
          {items.map((c) => (
            <div key={c.id} className={c.role === "user" ? "flex justify-end" : ""}>
              <div className={`max-w-[92%] rounded-xl px-4 py-3 text-sm leading-relaxed ${c.role === "user" ? "bg-[#FF5C00]/10 border border-[#FF5C00]/20" : "bg-muted/60 border border-border"}`}>
                <p className="whitespace-pre-wrap">{c.content}</p>
                {c.proposal && (
                  <div className="mt-3 rounded-lg border border-border bg-card p-3">
                    <Eyebrow>Proposed draft</Eyebrow>
                    <p className="mt-1 font-semibold">{c.proposal.subject}</p>
                    <p className="text-xs text-muted-foreground">{c.proposal.to.length} address{c.proposal.to.length === 1 ? "" : "es"}{c.proposal.cohortId ? " plus a cohort" : ""}</p>
                    <button data-testid={`button-use-proposal-${c.id}`} onClick={() => useProposal(c.proposal!)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C24600] dark:text-[#FF8A47] hover:underline"><FileEdit className="w-3.5 h-3.5" /> Open in draft form</button>
                  </div>
                )}
                <p className="mt-1.5 text-[10px] text-muted-foreground">{fmtDate(c.created_at)}</p>
              </div>
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Thinking</div>}
          <div ref={endRef} />
        </div>
        {error && <div className="px-5 pb-2"><Notice tone="error">{error}</Notice></div>}
        <form className="border-t border-border p-3 flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void send(input); }}>
          <textarea data-testid="input-assistant" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(input); } }} rows={2} placeholder="Ask or describe an email" className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40" />
          <button data-testid="button-send-assistant" type="submit" disabled={busy || !input.trim()} aria-label="Send to assistant" className="h-10 w-10 shrink-0 rounded-lg bg-[#FF5C00] text-white flex items-center justify-center disabled:opacity-40"><ArrowUp className="w-4 h-4" /></button>
        </form>
      </Panel>
      <Panel className="p-5">
        <DraftForm key={form.key} title="Compose a draft" initial={form.initial} cohorts={cohorts} onSaved={(id) => { setForm((f) => ({ key: f.key + 1 })); onSaved(id); }} />
      </Panel>
    </div>
  );
}
